import { motion } from 'motion/react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/lib/components/ui/accordion'

type FAQItemProps = {
  question: string
  answer: string[]
  defaultOpen?: boolean
}

const faqItems: FAQItemProps[] = [
  {
    question: 'How quickly can I see results with SOTA Navigator?',
    answer: [
      '• Immediate Impact: Most teams see results in their very first review',
      '• First Week: 5x faster review completion compared to manual processes',
      '• First Month: Full team adoption and workflow optimization',
      '• Training: 2-hour onboarding session gets your team ready to start',
      '• Support: Dedicated customer success manager for quick ramp-up',
    ],
  },
  {
    question: 'Will this work with our existing SOPs?',
    answer: [
      '• 100% Customizable: We adapt to your processes, not vice versa',
      '• SOP Integration: Direct mapping of your procedures into our system',
      '• Flexible Workflows: Custom templates matching your exact requirements',
      '• Change Management: We help document all process improvements',
      '• Validation: Full documentation for your quality system',
    ],
  },
  {
    question: 'Is the software validated for medical devices?',
    answer: [
      '• Full Validation Package: Ready for immediate implementation',
      '• Regulatory Compliance: Meets FDA 21 CFR Part 11 requirements',
      '• Audit Trail: Complete documentation of all user actions',
      '• Security: Project_role-based access control and data encryption',
      '• Track Record: Successfully passed numerous regulatory audits',
    ],
  },
  {
    question: 'What types of reviews can I automate?',
    answer: [
      '• SOTA Reports: Automated data extraction and analysis',
      '• PMCF Studies: Streamlined post-market surveillance',
      '• Clinical Evaluations: Comprehensive literature reviews',
      '• Safety Updates: Regular safety and performance monitoring',
      '• Custom Reviews: Flexible templates for any review type',
      '• Competitor Analysis: Market and technology surveillance',
    ],
  },
  {
    question: 'How do you ensure MDR compliance?',
    answer: [
      '• Built-in Requirements: All MDR criteria integrated into workflows',
      '• Automatic Checks: Compliance verification at each step',
      '• Documentation: Automated generation of compliant reports',
      '• Updates: Regular updates to match regulatory changes',
      '• Expert Support: Access to regulatory specialists',
      '• Audit Ready: Structured data for immediate audit response',
    ],
  },
  {
    question: 'What support do you provide?',
    answer: [
      '• Dedicated Success Manager: Your primary point of contact',
      '• Technical Support: 24/7 emergency assistance',
      '• Training: Regular sessions for new team members',
      '• Updates: Monthly feature releases and improvements',
      '• Community: Access to user group and best practices',
      '• Resources: Comprehensive documentation and guides',
    ],
  },
]

export const FAQSection = () => (
  <section className="relative bg-white py-24">
    {/* Subtle texture background */}
    <div className="pointer-events-none absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-[0.02] mix-blend-soft-light"></div>

    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16 text-center"
      >
        <h2 className="mb-4 text-4xl font-bold text-[#001871] md:text-5xl">Common Questions</h2>
        <p className="text-xl text-gray-600">
          Everything you need to know about transforming your literature review process.
        </p>
      </motion.div>

      <div className="space-y-4">
        <Accordion className="w-full">
          {faqItems.map((item, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="mb-4 overflow-hidden rounded-lg border border-gray-200"
            >
              <AccordionTrigger className="px-6 py-4 text-left text-lg font-semibold text-[#001871] hover:bg-gray-50 [&[data-state=open]>svg]:rotate-180">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="bg-gray-50 px-6 py-4">
                <div className="space-y-2">
                  {item.answer.map((point, idx) => (
                    <p key={idx} className="text-gray-600">
                      {point}
                    </p>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
)
